// src/components/data_visualizations/ConsideredSolutionsChartsOnly.jsx
import React, { useContext, useMemo, useState, useEffect } from 'react';
import { Box, Typography, Grid, Checkbox, FormControlLabel } from '@mui/material';
import {
  PieChart, Pie, Cell, Tooltip as RTooltip,
  LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer
} from 'recharts';
import { format, parse, eachMonthOfInterval, subMonths } from 'date-fns';
import { DataAndFilterContext } from '../../context/DataAndFilterContext';

const COLORS = ['#00C9A7', '#FF6B6B', '#1E90FF', '#FFD700', '#9B59B6'];

export default function ConsideredSolutionsChartsOnly() {
  const { filteredData, filters } = useContext(DataAndFilterContext);
  const [visibleCategories, setVisibleCategories] = useState([]);

  // Pie data
  const pieChartData = useMemo(() => {
    const categoryCount = {};
    filteredData.forEach((item) => {
      const category = item?.considered_solutions?.[0]?.considered_solution_category;
      if (category) categoryCount[category] = (categoryCount[category] || 0) + 1;
    });
    return Object.keys(categoryCount).map((category) => ({ name: category, value: categoryCount[category] }));
  }, [filteredData]);

  // Months in range
  const monthsInRange = useMemo(() => {
    const defaultEndDate = new Date();
    let startDate;
    let endDate = defaultEndDate;

    if (filters.dateRange === '6 months') {
      startDate = subMonths(endDate, 6);
    } else if (filters.dateRange === '3 months') {
      startDate = subMonths(endDate, 3);
    } else if (filters.dateRange === 'Custom' && filters.customDateRange.from && filters.customDateRange.to) {
      startDate = new Date(filters.customDateRange.from);
      endDate = new Date(filters.customDateRange.to);
    } else {
      // All time → find earliest
      const earliestDate = filteredData.reduce((earliest, item) => {
        const d = new Date(item.conversation_date);
        return d < earliest ? d : earliest;
      }, new Date());
      startDate = earliestDate;
    }

    return eachMonthOfInterval({ start: startDate, end: endDate }).map((month) =>
      format(month, "MMM ''yy")
    );
  }, [filters, filteredData]);

  // Line data
  const lineChartData = useMemo(() => {
    const monthly = {};
    filteredData.forEach((item) => {
      const category = item?.considered_solutions?.[0]?.considered_solution_category;
      const conversationMonth = format(parse(item.conversation_date, 'MM-dd-yyyy', new Date()), "MMM ''yy");
      if (!category) return;
      if (!monthly[conversationMonth]) monthly[conversationMonth] = {};
      monthly[conversationMonth][category] = (monthly[conversationMonth][category] || 0) + 1;
    });

    return monthsInRange.map((month) => {
      const row = { month };
      pieChartData.forEach((c) => {
        row[c.name] = monthly[month]?.[c.name] || 0;
      });
      return row;
    });
  }, [filteredData, monthsInRange, pieChartData]);

  // default visible → all series
  useEffect(() => {
    setVisibleCategories(pieChartData.map((d) => d.name));
  }, [pieChartData]);

  const toggle = (cat) =>
    setVisibleCategories((cur) =>
      cur.includes(cat) ? cur.filter((c) => c !== cat) : [...cur, cat]
    );

  return (
    <Grid container spacing={4} sx={{ mb: 4 }}>
      {/* Pie */}
      <Grid item xs={12} md={6}>
        <Typography variant="h6">Considered Solutions</Typography>
        <Typography variant="body2" gutterBottom>
          These are the solutions that customers considered using or used alongside their chosen solution.
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={pieChartData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={100}
              label={({ name }) => name}
            >
              {pieChartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <RTooltip />
          </PieChart>
        </ResponsiveContainer>
      </Grid>

      {/* Line */}
      <Grid item xs={12} md={6}>
        <Typography variant="h6">Monthly Changes in Considered Solutions</Typography>
        <Typography variant="body2" gutterBottom>
          Number of conversations mentioning the considered solutions
        </Typography>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={lineChartData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <RTooltip />
            {pieChartData.map((c, i) =>
              visibleCategories.includes(c.name) ? (
                <Line
                  key={c.name}
                  type="monotone"
                  dataKey={c.name}
                  stroke={COLORS[i % COLORS.length]}
                  activeDot={{ r: 8 }}
                />
              ) : null
            )}
          </LineChart>
        </ResponsiveContainer>

        {/* Series toggles */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', maxWidth: '100%', mt: 2 }}>
          {pieChartData.map((c, i) => (
            <FormControlLabel
              key={c.name}
              control={
                <Checkbox
                  checked={visibleCategories.includes(c.name)}
                  onChange={() => toggle(c.name)}
                  sx={{
                    color: COLORS[i % COLORS.length],
                    '&.Mui-checked': { color: COLORS[i % COLORS.length] }
                  }}
                />
              }
              label={c.name}
              sx={{ color: COLORS[i % COLORS.length], mr: 2, whiteSpace: 'nowrap' }}
            />
          ))}
        </Box>
      </Grid>
    </Grid>
  );
}
